import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Select,
  useToast,
  Alert,
  AlertIcon,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Box,
  Icon,
  AlertDescription,
  Spinner,
  Badge,
  Radio,
  RadioGroup
} from '@chakra-ui/react';
import { collection, query, where, onSnapshot, documentId } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { db, app } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { CreatableSelect, Select as MultiSelect } from 'chakra-react-select';
import { FiMail, FiEye, FiEyeOff, FiMessageSquare } from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import { syncTagOwnershipOnReassignment } from '../../utils/tagOwnership';
import { applyTagsBulk } from '../../utils/crmTags';
import { useTagRemovalConfirm } from './ConfirmTagRemovalDialog';
import RichTextEmailEditor from './Email/RichTextEmailEditor';
import AttachmentPicker from './Email/AttachmentPicker';
import EmailPreviewModal from './Email/EmailPreviewModal';
import DynamicVariableInsertButton from './Email/DynamicVariableInsertButton';
import SendTestSMSModal from './Dialpad/SendTestSMSModal';
import SmsMediaPicker from './Dialpad/SmsMediaPicker';
import { buildVariableMap, resolveTemplateVariables, insertTagIntoTextarea } from '../../utils/templateVariables';
import { buildReachRows, buildReachCsv, buildStandardContactsCsv, downloadCsvFile } from '../../utils/reachExport';

/**
 * Bulk Tag Modal
 * Adds tags to selected contacts without disturbing their existing tags, or
 * removes tags from them. Tags are shared with each contact's deals.
 */
export function BulkContactTagModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const [selectedTags, setSelectedTags] = useState([]);
  const [mode, setMode] = useState('add');
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const { getVisibleTags } = useCrmSettings();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const { confirmTagRemoval, dialog: removalDialog } = useTagRemovalConfirm();

  // Get whether user is admin for tag visibility
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  
  // Get visible tags for the current user
  const availableTags = useMemo(() => {
    const tags = getVisibleTags(currentUser?.uid, isCRMAdmin) || [];
    return tags.map(tag => ({
      value: tag.value,
      label: tag.label,
      color: tag.color || 'gray',
    }));
  }, [getVisibleTags, currentUser?.uid, isCRMAdmin]);

  const isRemoving = mode === 'remove';

  const handleUpdate = async () => {
    if (selectedTags.length === 0) {
      toast({
        title: 'Select at least one tag',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (isRemoving) {
      const contactCount = selectedContacts.length.toLocaleString();
      const confirmed = await confirmTagRemoval({
        title: `Remove ${selectedTags.length === 1 ? 'this tag' : 'these tags'} from ${contactCount} contacts?`,
        tags: selectedTags.map(t => t.label || t.value),
        description: `This removes ${selectedTags.length === 1 ? 'the tag' : 'these tags'} from ${contactCount} contact(s).`,
        note: 'Because tags are shared, their linked deals lose them too. This cannot be undone.',
        confirmLabel: `Remove from ${contactCount} contacts`,
      });

      if (!confirmed) return;
    }

    setIsUpdating(true);
    setProgress({ current: 0, total: selectedContacts.length });
    
    try {
      const tagValues = selectedTags.map(t => t.value);
      
      if (isSupabaseConfigured()) {
        // Chunked so each RPC call stays inside the statement timeout. The RPC
        // registers unknown tags, grants owners filter visibility, and mirrors
        // the result onto each contact's deals.
        const CHUNK_SIZE = 100;
        for (let i = 0; i < selectedContacts.length; i += CHUNK_SIZE) {
          const chunk = selectedContacts.slice(i, i + CHUNK_SIZE);
          await applyTagsBulk({
            contactIds: chunk,
            add: isRemoving ? [] : tagValues,
            remove: isRemoving ? tagValues : [],
            actorId: currentUser?.uid,
          });
          setProgress({ current: Math.min(i + CHUNK_SIZE, selectedContacts.length), total: selectedContacts.length });
        }

        // Add audit log (cap stored IDs to avoid oversized payloads)
        await supabase.from('crm_audit_logs').insert({
          action: isRemoving ? 'bulk_tag_remove' : 'bulk_tag_add',
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || currentUser?.email,
          entity_type: 'contact',
          details: {
            contactCount: selectedContacts.length,
            [isRemoving ? 'tagsRemoved' : 'tagsAdded']: tagValues,
            contactIds: selectedContacts.slice(0, 200),
            truncated: selectedContacts.length > 200,
          },
        });
      }

      toast({
        title: isRemoving ? 'Tags removed' : 'Tags added',
        description: `${isRemoving ? 'Removed' : 'Added'} ${tagValues.length} tag(s) ${isRemoving ? 'from' : 'to'} ${selectedContacts.length.toLocaleString()} contacts`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      setSelectedTags([]);
      onClose();
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        title: isRemoving ? 'Error removing tags' : 'Error adding tags',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isRemoving ? 'Remove Tags (Bulk)' : 'Add Tags (Bulk)'}</ModalHeader>
        <ModalCloseButton isDisabled={isUpdating} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <RadioGroup value={mode} onChange={setMode} isDisabled={isUpdating}>
              <HStack spacing={6}>
                <Radio value="add">Add tags</Radio>
                <Radio value="remove">Remove tags</Radio>
              </HStack>
            </RadioGroup>

            <Alert status={isRemoving ? 'warning' : 'info'} size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                {isRemoving ? (
                  <>Tags will be <strong>removed</strong> from {selectedContacts.length.toLocaleString()} contacts and their deals</>
                ) : (
                  <>Tags will be <strong>added</strong> to {selectedContacts.length.toLocaleString()} contacts (existing tags are preserved)</>
                )}
              </Text>
            </Alert>

            {isUpdating && progress.total > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">{isRemoving ? 'Removing tags...' : 'Adding tags...'}</Text>
                  <Text fontSize="sm" fontWeight="medium">{progressPercent}%</Text>
                </HStack>
                <Box h="8px" bg="gray.200" borderRadius="full" overflow="hidden">
                  <Box h="100%" bg="blue.500" borderRadius="full" w={`${progressPercent}%`} transition="width 0.3s ease" />
                </Box>
              </Box>
            )}

            <Text fontSize="sm" fontWeight="medium">
              {isRemoving ? 'Select tags to remove:' : 'Select tags to add:'}
            </Text>
            <CreatableSelect
              isMulti
              value={selectedTags}
              onChange={setSelectedTags}
              options={availableTags}
              placeholder={isRemoving ? 'Search tags...' : 'Search or create tags...'}
              isDisabled={isUpdating}
              isValidNewOption={() => !isRemoving}
              formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                }),
                menu: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                  zIndex: 9999,
                }),
                multiValue: (provided, state) => ({
                  ...provided,
                  backgroundColor: `var(--chakra-colors-${state.data.color || 'gray'}-100)`,
                }),
                multiValueLabel: (provided, state) => ({
                  ...provided,
                  color: `var(--chakra-colors-${state.data.color || 'gray'}-800)`,
                }),
              }}
            />
            <Text fontSize="xs" color="gray.500">
              {isRemoving
                ? 'Tags are shared between a contact and its deals, so removing here removes it from both.'
                : 'Type to search existing tags or create new custom tags'}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme={isRemoving ? 'red' : 'blue'}
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText={`${isRemoving ? 'Removing' : 'Adding'}... ${progressPercent}%`}
            isDisabled={selectedTags.length === 0}
          >
            {isRemoving ? 'Remove from' : 'Add to'} {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>

      {removalDialog}
    </Modal>
  );
}

/**
 * Bulk Contact Type Update Modal
 */
export function BulkContactTypeModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const { contactTypes } = useCrmSettings();
  const toast = useToast();

  const handleUpdate = async () => {
    if (selectedTypes.length === 0) {
      toast({
        title: 'Select at least one contact type',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsUpdating(true);
    setProgress({ current: 0, total: selectedContacts.length });
    
    try {
      const types = selectedTypes.map(t => t.value);
      
      if (isSupabaseConfigured()) {
        const CHUNK = 100;
        for (let i = 0; i < selectedContacts.length; i += CHUNK) {
          const chunk = selectedContacts.slice(i, i + CHUNK);
          const { error } = await supabase
            .from('crm_contacts')
            // primary_contact_type must be replaced along with contact_types —
            // a stale primary (e.g. 'borrower' left over from an import) still
            // renders as a phantom type badge even after the array is replaced.
            .update({ contact_types: types, primary_contact_type: types[0] })
            .in('id', chunk);
          if (error) throw error;
          setProgress({ current: Math.min(i + CHUNK, selectedContacts.length), total: selectedContacts.length });
        }
      }

      toast({
        title: 'Contact types updated',
        description: `Updated ${selectedContacts.length.toLocaleString()} contacts`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      setSelectedTypes([]);
      onClose();
    } catch (error) {
      console.error('Error updating contact types:', error);
      toast({
        title: 'Error updating contact types',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Contact Types (Bulk)</ModalHeader>
        <ModalCloseButton isDisabled={isUpdating} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" size="sm">
              <AlertIcon />
              <Text fontSize="sm">This will REPLACE existing contact types for {selectedContacts.length.toLocaleString()} contacts</Text>
            </Alert>

            {isUpdating && progress.total > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">Updating...</Text>
                  <Text fontSize="sm" fontWeight="medium">{progressPercent}%</Text>
                </HStack>
                <Box h="8px" bg="gray.200" borderRadius="full" overflow="hidden">
                  <Box h="100%" bg="blue.500" borderRadius="full" w={`${progressPercent}%`} transition="width 0.3s ease" />
                </Box>
              </Box>
            )}

            <Text fontSize="sm" fontWeight="medium">Select new contact types:</Text>
            <MultiSelect
              isMulti
              value={selectedTypes}
              onChange={setSelectedTypes}
              options={contactTypes?.map(type => ({
                value: type.value,
                label: type.label,
              })) || []}
              placeholder="Select contact types..."
              isDisabled={isUpdating}
            />
            <Text fontSize="xs" color="gray.600">
              First selected type will be the primary type
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText={`Updating... ${progressPercent}%`}
          >
            Update {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Ownership Transfer Modal
 */
export function BulkContactOwnershipModal({ isOpen, onClose, selectedContacts, crmUsers, onSuccess }) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleUpdate = async () => {
    if (!selectedUserId) {
      toast({
        title: 'Select a user',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsUpdating(true);
    setProgress({ current: 0, total: selectedContacts.length, phase: 'Preparing transfer...' });
    
    try {
      const assignedUser = crmUsers.find(u => u.uid === selectedUserId);
      const userName = assignedUser?.name || assignedUser?.Name || 'user';
      
      // Track deals count for the toast message
      let dealsCount = 0;
      
      if (isSupabaseConfigured()) {
        const CHUNK = 100;

        // Update contacts in Supabase (chunked)
        for (let i = 0; i < selectedContacts.length; i += CHUNK) {
          const chunk = selectedContacts.slice(i, i + CHUNK);
          const { error: contactsError } = await supabase
            .from('crm_contacts')
            .update({ owner_id: selectedUserId })
            .in('id', chunk);
          if (contactsError) throw contactsError;
          setProgress({ current: Math.min(i + CHUNK, selectedContacts.length), total: selectedContacts.length, phase: 'Transferring contacts...' });
        }

        // Also transfer any deals associated with these contacts (chunked)
        let transferredDeals = [];
        for (let i = 0; i < selectedContacts.length; i += CHUNK) {
          const chunk = selectedContacts.slice(i, i + CHUNK);
          const { data, error: dealsError } = await supabase
            .from('crm_deals')
            .update({ owner_id: selectedUserId })
            .in('contact_id', chunk)
            .select('id');
          if (dealsError) throw dealsError;
          if (data) transferredDeals.push(...data);
        }
        dealsCount = transferredDeals.length;

        // Add audit log
        await supabase.from('crm_audit_logs').insert({
          action: 'bulk_contact_transfer',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'contact',
          details: {
            contactCount: selectedContacts.length,
            dealCount: dealsCount,
            transferredTo: userName,
            transferredToId: selectedUserId,
            contactIds: selectedContacts.slice(0, 200),
            truncated: selectedContacts.length > 200,
          },
        });

        // Sync tag ownership so the new owner can filter by existing tags
        // This ensures users can filter by tags on their reassigned contacts/deals
        await syncTagOwnershipOnReassignment('contact', selectedContacts, selectedUserId);
        if (transferredDeals && transferredDeals.length > 0) {
          const dealIds = transferredDeals.map(d => d.id);
          await syncTagOwnershipOnReassignment('deal', dealIds, selectedUserId);
        }
      }

      toast({
        title: 'Contacts transferred',
        description: `Transferred ${selectedContacts.length} contacts and ${dealsCount} deals to ${userName}`,
        status: 'success',
        duration: 5000,
      });

      onSuccess();
      setSelectedUserId('');
      onClose();
    } catch (error) {
      console.error('Error transferring contacts:', error);
      toast({
        title: 'Error transferring contacts',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
      setProgress({ current: 0, total: 0, phase: '' });
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Transfer Ownership (Bulk)</ModalHeader>
        <ModalCloseButton isDisabled={isUpdating} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                This will transfer {selectedContacts.length.toLocaleString()} contacts and their associated deals
              </Text>
            </Alert>

            {isUpdating && progress.total > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">{progress.phase}</Text>
                  <Text fontSize="sm" fontWeight="medium">{progressPercent}%</Text>
                </HStack>
                <Box 
                  h="8px" 
                  bg="gray.200" 
                  borderRadius="full" 
                  overflow="hidden"
                >
                  <Box 
                    h="100%" 
                    bg="blue.500" 
                    borderRadius="full"
                    w={`${progressPercent}%`}
                    transition="width 0.3s ease"
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {progress.current.toLocaleString()} of {progress.total.toLocaleString()} operations
                </Text>
              </Box>
            )}

            <Select
              placeholder="Select user"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              isDisabled={isUpdating}
            >
              {crmUsers.map((user) => (
                <option key={user.uid} value={user.uid}>
                  {user.name || user.Name}
                </option>
              ))}
            </Select>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText={`Transferring... ${progressPercent}%`}
          >
            Transfer {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Record Scope Modal (admin-only — gate the trigger with isCRMAdmin)
 * Marks selected contacts as company records (visible to every CRM admin via
 * the visibility predicate) or converts them back to personal records.
 * Writes with _updated_by='import' so the audit trigger emits no
 * contact.updated events — scope flips must never fire automations.
 */
export function BulkContactRecordScopeModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const [scope, setScope] = useState('company');
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      if (isSupabaseConfigured()) {
        const CHUNK = 100;
        for (let i = 0; i < selectedContacts.length; i += CHUNK) {
          const chunk = selectedContacts.slice(i, i + CHUNK);
          const { error } = await supabase
            .from('crm_contacts')
            .update({ record_scope: scope, _updated_by: 'import' })
            .in('id', chunk);
          if (error) throw error;
        }

        await supabase.from('crm_audit_logs').insert({
          action: scope === 'company' ? 'bulk_record_scope_company' : 'bulk_record_scope_personal',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'contact',
          details: {
            contactCount: selectedContacts.length,
            newScope: scope,
            contactIds: selectedContacts.slice(0, 200),
            truncated: selectedContacts.length > 200,
          },
        });
      }

      toast({
        title: scope === 'company' ? 'Marked as company records' : 'Converted to personal records',
        description: `${selectedContacts.length.toLocaleString()} contacts updated`,
        status: 'success',
        duration: 4000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating record scope:', error);
      toast({
        title: 'Error updating record scope',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Set Record Scope (Bulk)</ModalHeader>
        <ModalCloseButton isDisabled={isUpdating} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                Company records stay on their owner's book but become visible to
                every CRM admin. This never enrolls anyone in campaigns.
              </Text>
            </Alert>
            <RadioGroup value={scope} onChange={setScope}>
              <VStack align="stretch" spacing={2}>
                <Radio value="company" isDisabled={isUpdating}>
                  <HStack spacing={2}>
                    <Text>Company record</Text>
                    <Badge colorScheme="purple">visible to all CRM admins</Badge>
                  </HStack>
                </Radio>
                <Radio value="user" isDisabled={isUpdating}>
                  <HStack spacing={2}>
                    <Text>Personal record</Text>
                    <Badge colorScheme="gray">owner + shares only</Badge>
                  </HStack>
                </Radio>
              </VStack>
            </RadioGroup>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleUpdate} isLoading={isUpdating}>
            Update {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Delete Modal
 */
export function BulkDeleteContactsModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleDelete = async () => {
    setIsDeleting(true);
    setProgress({ current: 0, total: selectedContacts.length });
    
    try {
      if (isSupabaseConfigured()) {
        // Delete contacts in Supabase (chunked to avoid URL/timeout limits)
        const CHUNK = 100;
        for (let i = 0; i < selectedContacts.length; i += CHUNK) {
          const chunk = selectedContacts.slice(i, i + CHUNK);
          const { error } = await supabase
            .from('crm_contacts')
            .delete()
            .in('id', chunk);
          if (error) throw error;
          setProgress({ current: Math.min(i + CHUNK, selectedContacts.length), total: selectedContacts.length });
        }

        // Add audit log
        await supabase.from('crm_audit_logs').insert({
          action: 'bulk_delete',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'contact',
          details: {
            contactCount: selectedContacts.length,
            contactIds: selectedContacts.slice(0, 200),
            truncated: selectedContacts.length > 200,
          },
        });
      }

      toast({
        title: 'Contacts deleted',
        description: `Deleted ${selectedContacts.length.toLocaleString()} contacts`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting contacts:', error);
      toast({
        title: 'Error deleting contacts',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isDeleting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Contacts (Bulk)</ModalHeader>
        <ModalCloseButton isDisabled={isDeleting} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="error">
              <AlertIcon />
              <Text fontSize="sm">
                This will permanently delete {selectedContacts.length.toLocaleString()} contacts and all associated data. 
                This action cannot be undone.
              </Text>
            </Alert>
            
            {isDeleting && progress.total > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">Deleting...</Text>
                  <Text fontSize="sm" fontWeight="medium">{progressPercent}%</Text>
                </HStack>
                <Box h="8px" bg="gray.200" borderRadius="full" overflow="hidden">
                  <Box h="100%" bg="red.500" borderRadius="full" w={`${progressPercent}%`} transition="width 0.3s ease" />
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isLoading={isDeleting}
            loadingText={`Deleting... ${progressPercent}%`}
          >
            Delete {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Export Modal
 *
 * Offers two formats:
 * - Standard CSV: legacy 8-column export (headers unchanged)
 * - Reach App Template: CSV shaped for import into the Reach texting app
 *   (see src/utils/reachExport.js and docs/Reach Docs.md)
 *
 * Contacts are re-fetched by ID via `fetchContactsByIds`, so ALL selected
 * contacts export — not just the ones on the currently visible table page.
 */
export function BulkExportContactsModal({ isOpen, onClose, selectedContacts, fetchContactsByIds }) {
  const toast = useToast();
  const [exportFormat, setExportFormat] = useState('standard');
  const [isExporting, setIsExporting] = useState(false);

  const selectionCount = selectedContacts?.length || 0;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Full fetch by IDs — covers every selected contact across all pages
      const exportContacts = await fetchContactsByIds(selectedContacts);
      const dateStamp = new Date().toISOString().split('T')[0];

      if (exportFormat === 'reach') {
        const { rows, skippedNoPhone, duplicatesRemoved } = buildReachRows(exportContacts);
        if (rows.length === 0) {
          toast({
            title: 'Nothing to export',
            description: 'None of the selected contacts have a phone number. The Reach template requires one per contact.',
            status: 'error',
            duration: 6000,
          });
          return;
        }
        downloadCsvFile(buildReachCsv(rows), `reach-contacts-${dateStamp}.csv`);
        const summaryParts = [`Exported ${rows.length.toLocaleString()} contacts for Reach`];
        if (skippedNoPhone > 0) summaryParts.push(`${skippedNoPhone.toLocaleString()} skipped (no phone)`);
        if (duplicatesRemoved > 0) summaryParts.push(`${duplicatesRemoved.toLocaleString()} duplicate phone numbers removed`);
        toast({
          title: 'Export complete',
          description: summaryParts.join(' • '),
          status: 'success',
          duration: 6000,
        });
      } else {
        downloadCsvFile(buildStandardContactsCsv(exportContacts), `contacts-export-${dateStamp}.csv`);
        toast({
          title: 'Export complete',
          description: `Exported ${exportContacts.length.toLocaleString()} contacts`,
          status: 'success',
          duration: 3000,
        });
      }

      onClose();
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast({
        title: 'Error exporting',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Export Contacts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {selectionCount > 0 ? (
              <Alert status="info">
                <AlertIcon />
                <Text fontSize="sm">
                  Export {selectionCount.toLocaleString()} selected contacts to CSV
                </Text>
              </Alert>
            ) : (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  No contacts selected. Select contacts in the table first — the header checkbox selects everything matching your filters.
                </Text>
              </Alert>
            )}
            <RadioGroup value={exportFormat} onChange={setExportFormat}>
              <VStack align="start" spacing={3}>
                <Radio value="standard">
                  <Text fontSize="sm" fontWeight="medium">Standard CSV</Text>
                  <Text fontSize="xs" color="gray.500">
                    Core contact fields: name, email, phone, company, job title, source, tags
                  </Text>
                </Radio>
                <Radio value="reach">
                  <Text fontSize="sm" fontWeight="medium">Reach App Template</Text>
                  <Text fontSize="xs" color="gray.500">
                    Formatted for import into the Reach texting app. Contacts without a phone number are skipped.
                  </Text>
                </Radio>
              </VStack>
            </RadioGroup>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isExporting}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleExport}
            isLoading={isExporting}
            loadingText="Exporting..."
            isDisabled={selectionCount === 0}
          >
            Export {selectionCount.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Email Modal
 */
export function BulkEmailModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const attachUploadRef = React.useRef(null);
  const editorRef = React.useRef(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [from, setFrom] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const queueListenerRef = React.useRef(null);
  
  const toast = useToast();
  const { currentUser, SuperAdmin, Roles } = useAuth();

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const isSendingAsOtherUser = from && userEmail && from.toLowerCase() !== userEmail.toLowerCase();

  // Get current user's email on mount
  useEffect(() => {
    if (currentUser?.email) {
      setUserEmail(currentUser.email);
    }
  }, [currentUser]);

  // Pre-populate FROM when modal opens
  useEffect(() => {
    if (isOpen) {
      const userFromEmail = userEmail || currentUser?.email || '';
      setFrom(userFromEmail);
      setReplyTo(userFromEmail);
    }
  }, [isOpen, userEmail, currentUser]);

  // Prevent non-admins from sending as other users
  useEffect(() => {
    if (!isCRMAdmin && from && userEmail && from.toLowerCase() !== userEmail.toLowerCase()) {
      setFrom(userEmail);
      toast({
        title: 'Permission required',
        description: 'Only admins can send emails as other users',
        status: 'warning',
        duration: 3000,
      });
    }
  }, [from, userEmail, isCRMAdmin, toast]);

  // Load email templates from Firestore
  useEffect(() => {
    const templatesRef = collection(db, 'emailTemplates');
    const q = query(templatesRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = [];
      snapshot.forEach((doc) => {
        templatesList.push({ id: doc.id, ...doc.data() });
      });
      setTemplates(templatesList);
      setLoadingTemplates(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject || '');
      setBody(template.body || '');
    }
  };

  const handleSend = async () => {
    if (!from || !from.trim()) {
      toast({ title: 'Missing sender', description: 'Please specify a From email address', status: 'warning' });
      return;
    }
    if (!subject.trim()) {
      toast({ title: 'Subject is required', status: 'warning' });
      return;
    }
    if (!body.trim()) {
      toast({ title: 'Body is required', status: 'warning' });
      return;
    }

    setIsSending(true);
    try {
      console.log('Starting bulk email request...', { 
        recipientsCount: selectedContacts.length,
        from,
        replyTo
      });

      const functions = getFunctions(app, 'us-east1');
      const queueBulkEmails = httpsCallable(functions, 'queueBulkEmails');
      // Deliberately no templateId: the template dropdown only inserts content
      // into the editor. What the user sees in subject/body is exactly what
      // sends — the backend must never re-fetch the original template.
      const result = await queueBulkEmails({
        recipients: selectedContacts,
        subject,
        body,
        fromEmail: from.trim(),
        replyTo: replyTo.trim() || null,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      console.log('Bulk email response:', result.data);

      if (result.data.success) {
        toast({
          title: 'Emails Queued',
          description: `Successfully queued ${result.data.queuedCount} emails. They will be sent at a rate of 10 per 2 minutes.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Clean up any prior listener before setting up a new one
        if (queueListenerRef.current) {
          queueListenerRef.current();
          queueListenerRef.current = null;
        }

        if (result.data.taskIds && result.data.taskIds.length > 0) {
          const chunks = [];
          const MAX_IN_FILTER = 10;
          for (let i = 0; i < result.data.taskIds.length; i += MAX_IN_FILTER) {
            chunks.push(result.data.taskIds.slice(i, i + MAX_IN_FILTER));
          }

          if (chunks.length > 0) {
            const q = query(
              collection(db, 'mail_queue'),
              where(documentId(), 'in', chunks[0])
            );

            const unsubscribe = onSnapshot(q, (snapshot) => {
              snapshot.docChanges().forEach((change) => {
                const data = change.doc.data();
                console.log(`[Email Task ${change.doc.id}] Status: ${data.status}`, data);
                if (data.status === 'error') {
                  console.error(`[Email Task ${change.doc.id}] Error details:`, data.error);
                }
              });
            });

            queueListenerRef.current = unsubscribe;
            setTimeout(() => {
              if (queueListenerRef.current === unsubscribe) {
                unsubscribe();
                queueListenerRef.current = null;
              }
            }, 5 * 60 * 1000);
          }
        }

        onSuccess();
        setSubject('');
        setBody('');
        setSelectedTemplate('');
        setAttachments([]);
        onClose();
      }
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      toast({
        title: 'Error sending emails',
        description: error.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Icon as={FiMail} />
            <Text>Send Bulk Email</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                Sending to {selectedContacts.length} contacts. Emails are queued and sent at a rate of 10 every 2 minutes.
                <br/>
                <strong>Variables like {'{{firstName}}'} will be replaced for each contact.</strong>
              </Text>
            </Alert>

            {/* Admin Warning: Sending as another user */}
            {isSendingAsOtherUser && isCRMAdmin && (
              <Alert status="warning" borderRadius="md" size="sm">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  <Text fontWeight="bold">Sending as another user</Text>
                  <Text>
                    Emails will be sent from <strong>{from}</strong> with their signature.
                  </Text>
                </AlertDescription>
              </Alert>
            )}

            {/* From Field */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">From</FormLabel>
              <Input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="your-email@example.com"
                size="sm"
                isReadOnly={!isCRMAdmin}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {isCRMAdmin 
                  ? "Email will be sent from this address with their Gmail signature."
                  : "You can only send from your own email address."}
              </Text>
            </FormControl>

            {/* Reply-To Field */}
            <FormControl>
              <FormLabel fontSize="sm">Reply-To (optional)</FormLabel>
              <Input
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="reply-to@example.com"
                size="sm"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                When recipient clicks Reply, this address will be used. Defaults to From address.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Template</FormLabel>
              {loadingTemplates ? (
                <HStack p={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Loading templates...</Text>
                </HStack>
              ) : (
                <Select
                  placeholder="Select a template"
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  size="sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Variables: {'{{firstName}} {{lastName}}'}
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Subject</FormLabel>
              <Input 
                value={subject} 
                onChange={(e) => setSubject(e.target.value)} 
                placeholder="Email subject..."
                size="sm"
              />
            </FormControl>

            <FormControl isRequired>
              <HStack justify="space-between" mb={1}>
                <FormLabel fontSize="sm" mb={0}>Body</FormLabel>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<FiEye />}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </Button>
              </HStack>
              <RichTextEmailEditor
                ref={editorRef}
                value={body}
                onChange={setBody}
                placeholder="Compose your email..."
                toolbarExtra={<DynamicVariableInsertButton editor={editorRef.current} />}
                onFileDrop={(file) => attachUploadRef.current?.(file)}
              />
            </FormControl>

            <AttachmentPicker attachments={attachments} onChange={setAttachments} uploadFileRef={attachUploadRef} />

            <EmailPreviewModal
              isOpen={showPreview}
              onClose={() => setShowPreview(false)}
              body={body}
              subject={subject}
              fromEmail={from || ''}
              toLabel="to your recipients"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isSending}
            loadingText="Queuing..."
            leftIcon={<FiMail />}
            isDisabled={!from || !from.trim() || !subject || !body}
          >
            Send Bulk Emails
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk SMS Modal
 *
 * Sends personalized Dialpad SMS to selected contacts using a queue-based
 * backend. Supports SMS templates with {{variable}} replacement.
 * Messages are queued and processed at ~25/min to stay within Dialpad rate limits.
 */
export function BulkSMSModal({ isOpen, onClose, selectedContacts, onSuccess }) {
  const [message, setMessage] = useState('');
  const [media, setMedia] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [loadingTest, setLoadingTest] = useState(false);
  const messageRef = React.useRef(null);

  const toast = useToast();
  const { currentUser } = useAuth();

  // Load SMS templates from Firestore
  useEffect(() => {
    const templatesRef = collection(db, 'smsTemplates');
    const q = query(templatesRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = [];
      snapshot.forEach((docSnap) => {
        templatesList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setTemplates(templatesList);
      setLoadingTemplates(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessage(template.body || template.content || template.message || '');
      if (template.media && template.media.url) {
        setMedia(template.media);
      }
    }
  };

  const handleSend = async () => {
    if (!message.trim() && !media) {
      toast({ title: 'Message or image is required', status: 'warning', duration: 2000 });
      return;
    }

    setIsSending(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const queueBulkSMS = httpsCallable(functions, 'queueBulkSMS');
      // Deliberately no templateId: the template dropdown only inserts content
      // into the editor (media from a template is copied into local state on
      // selection). What the user sees is exactly what sends.
      const result = await queueBulkSMS({
        recipients: selectedContacts,
        message: message.trim(),
        mediaUrl: media?.url || null,
      });

      if (result.data.success) {
        toast({
          title: media ? 'MMS Queued' : 'SMS Queued',
          description: `Successfully queued ${result.data.queuedCount} messages. They will be sent at a rate of ~25 per minute.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        onSuccess?.();
        setMessage('');
        setMedia(null);
        setSelectedTemplate('');
        onClose();
      }
    } catch (error) {
      console.error('Error queuing bulk SMS:', error);

      let errorMessage = error.message;
      if (error.message?.includes('not enabled') || error.message?.includes('failed-precondition')) {
        errorMessage = 'Dialpad is not enabled for your account. Please enable it in CRM Settings.';
      }

      toast({
        title: 'Error queuing SMS',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenTestModal = async () => {
    if (!message.trim() && !media) {
      toast({ title: 'Enter a message or add an image first', status: 'warning', duration: 2000 });
      return;
    }
    setLoadingTest(true);
    try {
      // Resolve with the first selected contact's full data — same variable
      // map the backend queue processor uses, so the test matches real sends.
      let contact = null;
      let deal = null;
      if (selectedContacts.length > 0 && isSupabaseConfigured()) {
        const { data: contactRow } = await supabase
          .from('crm_contacts')
          .select('*')
          .eq('id', selectedContacts[0])
          .single();
        contact = contactRow;

        const { data: deals } = await supabase
          .from('crm_deals')
          .select('*')
          .eq('contact_id', selectedContacts[0])
          .order('created_at', { ascending: false })
          .limit(1);
        deal = deals?.[0] || null;
      }

      const resolved = resolveTemplateVariables(message, buildVariableMap({
        contact,
        deal,
        sender: {
          name: currentUser?.displayName || '',
          email: currentUser?.email || '',
          phone: '',
        },
      }));
      setTestMessage(resolved);
      setIsTestModalOpen(true);
    } catch (error) {
      console.error('Error loading test data:', error);
      toast({ title: 'Error loading contact data for test', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setLoadingTest(false);
    }
  };

  // Preview replaces variables with sample data using the same map as real sends
  const previewMessage = useMemo(() => {
    if (!message) return '';
    const sampleVars = buildVariableMap({
      contact: {
        firstName: 'John',
        lastName: 'Smith',
        email: 'john@example.com',
        phone: '(555) 555-1234',
        company: 'Acme Corp',
      },
      deal: {
        product: 'Conventional 30yr',
        amount: 350000,
        dealName: 'Smith Purchase',
        loanNumber: '123456789',
        lender: 'United Wholesale',
        loanPurpose: 'Purchase',
        propertyAddress: { street: '123 Main St', city: 'Miami', state: 'FL', zip: '33101' },
        estimatedClosingDate: '2026-04-15',
      },
      sender: {
        name: currentUser?.displayName || 'Loan Officer',
        email: currentUser?.email || '',
        phone: '',
      },
    });
    return resolveTemplateVariables(message, sampleVars);
  }, [message, currentUser]);

  const charCount = message.length;
  const previewCharCount = previewMessage.length;
  const segmentCount = previewCharCount > 0 ? Math.ceil(previewCharCount / 160) : 0;
  const charColor = charCount > 160 ? 'orange' : charCount > 140 ? 'yellow' : 'gray';

  return (
  <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Icon as={FiMessageSquare} />
            <Text>Send Bulk SMS</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                Sending to {selectedContacts.length.toLocaleString()} contacts via Dialpad.
                Messages are queued and sent at ~25 per minute.
                <br />
                <strong>
                  Variables like {'{{firstName}}'} are replaced for each contact — every
                  contact, deal, and sender field is supported.
                </strong>
              </Text>
            </Alert>

            <FormControl>
              <FormLabel fontSize="sm">Template</FormLabel>
              {loadingTemplates ? (
                <HStack p={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Loading templates...</Text>
                </HStack>
              ) : templates.length > 0 ? (
                <Select
                  placeholder="Select a template (optional)"
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  size="sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Text fontSize="xs" color="gray.400">No SMS templates configured</Text>
              )}
            </FormControl>

            <FormControl isRequired>
              <HStack justify="space-between" mb={1}>
                <FormLabel fontSize="sm" mb={0}>Message</FormLabel>
                <HStack spacing={2}>
                  <Badge colorScheme={charColor}>{charCount} chars</Badge>
                  {segmentCount > 1 && (
                    <Badge colorScheme="orange">{segmentCount} segments</Badge>
                  )}
                  <DynamicVariableInsertButton
                    onInsert={(tag) => insertTagIntoTextarea(messageRef, message, tag, setMessage)}
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={showPreview ? <FiEyeOff /> : <FiEye />}
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Hide Preview' : 'Preview'}
                  </Button>
                </HStack>
              </HStack>
              <Textarea
                ref={messageRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message... Use {{firstName}} for personalization."
                rows={6}
                maxLength={320}
              />
              {previewCharCount > 160 && (
                <Text fontSize="xs" color="orange.600" mt={1}>
                  After variable replacement, messages may be sent as {segmentCount} parts (~{previewCharCount} chars).
                  Multi-part SMS can increase spam risk.
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Image (optional)</FormLabel>
              <SmsMediaPicker media={media} onChange={setMedia} isDisabled={isSending} />
              {media && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Each recipient receives this as an MMS (same image for everyone).
                </Text>
              )}
            </FormControl>

            {showPreview && previewMessage && (
              <Box bg="gray.50" borderRadius="md" p={3} borderWidth="1px" borderColor="gray.200">
                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                  PREVIEW (sample data)
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">{previewMessage}</Text>
                <HStack mt={2} spacing={2}>
                  <Badge colorScheme="blue">{previewCharCount} chars</Badge>
                  <Badge colorScheme={segmentCount > 1 ? 'orange' : 'green'}>
                    {segmentCount} SMS segment{segmentCount !== 1 ? 's' : ''}
                  </Badge>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            colorScheme="blue"
            onClick={handleOpenTestModal}
            isLoading={loadingTest}
            loadingText="Loading..."
            isDisabled={!message.trim() && !media}
            mr={2}
          >
            Send Test SMS
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSend}
            isLoading={isSending}
            loadingText="Queuing..."
            leftIcon={<FiMessageSquare />}
            isDisabled={!message.trim() && !media}
          >
            Send to {selectedContacts.length.toLocaleString()} Contacts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <SendTestSMSModal
      isOpen={isTestModalOpen}
      onClose={() => setIsTestModalOpen(false)}
      message={testMessage}
      mediaUrl={media?.url || null}
    />
  </>
  );
}
